import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Phone, LogIn } from "lucide-react";
import { Button } from "../ui/button";

const WHATSAPP_NUMBER = "56929894282";
const WHATSAPP_MESSAGE = encodeURIComponent("Hola Odontofix, quiero cotizar mantención y agendar visita.");
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
import { logoColor as LOGO_URL } from "@/lib/logos";

const navLinks = [
  { name: "Inicio", path: "/" },
  { name: "Servicios", path: "/servicios" },
  { name: "App", path: "/app" },
  { name: "Productos", path: "/productos" },
  { name: "Nosotros", path: "/nosotros" },
  { name: "FAQ", path: "/faq" },
  { name: "Contacto", path: "/contacto" },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className="sticky top-0 z-40 w-full border-b border-gray-100 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/80"
      data-testid="main-header"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-16 md:h-18 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Odontofix - Mantención de equipos clínicos" 
              className="h-9 md:h-10 w-auto"
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50 hover:text-[#0FB4B7] focus:outline-none focus:ring-2 focus:ring-[#0FB4B7]/50 ${
                  isActive(link.path) ? "text-[#0FB4B7] bg-[#0FB4B7]/5" : "text-[#1F2937]"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/cliente">
              <Button 
                variant="outline"
                className="border-[#0FB4B7] text-[#0FB4B7] hover:bg-[#0FB4B7] hover:text-white h-10 px-4 font-medium transition-colors"
                data-testid="header-cliente-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Portal Clientes
              </Button>
            </Link>
            <Link to="/tecnico/login">
              <Button 
                variant="ghost"
                className="text-gray-500 hover:text-[#384091] h-10 px-3 font-medium transition-colors"
                data-testid="header-login-btn"
              >
                Técnicos
              </Button>
            </Link>
            <Link to="/contacto">
              <Button 
                variant="outline"
                className="border-[#384091] text-[#384091] hover:bg-[#384091] hover:text-white h-10 px-4 font-medium transition-colors"
                data-testid="header-contact-btn"
              >
                <Phone className="h-4 w-4 mr-2" />
                Contacto
              </Button>
            </Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-10 px-5 font-medium shadow-sm hover:shadow-md transition-all"
                data-testid="header-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Cotizar por WhatsApp
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0FB4B7]/50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6 text-[#1F2937]" /> : <Menu className="h-6 w-6 text-[#1F2937]" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-in slide-in-from-top-2 duration-200" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-[#0FB4B7]/10 text-[#0FB4B7]"
                      : "text-[#1F2937] hover:bg-gray-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-gray-100 px-4">
              <Link to="/cliente" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  variant="outline"
                  className="w-full border-[#0FB4B7] text-[#0FB4B7] h-12 font-medium"
                  data-testid="mobile-cliente-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Portal Clientes
                </Button>
              </Link>
              <Link to="/tecnico/login" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  variant="outline"
                  className="w-full border-gray-300 text-gray-600 hover:bg-gray-50 h-12 font-medium"
                  data-testid="mobile-login-btn"
                >
                  Acceso Técnicos
                </Button>
              </Link>
              <Link to="/contacto" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  variant="outline"
                  className="w-full border-[#384091] text-[#384091] hover:bg-[#384091] hover:text-white h-12 font-medium"
                  data-testid="mobile-contact-btn"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Contacto
                </Button>
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="w-full bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-12 font-medium"
                  data-testid="mobile-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Cotizar por WhatsApp
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
